import { useCallback, useEffect, useState } from 'react';
// @mui
import { useTheme } from '@mui/material/styles';
import { Box, Stack, Typography, Button } from '@mui/material';
import Drawer, { drawerClasses } from '@mui/material/Drawer';

import { useResponsive } from 'src/hooks/use-responsive';
import { useSelector, useDispatch } from 'src/store';
import { closeDrawer, openEditPage, closeEditPage } from 'src/store/reducers/drawer';

import AccountView, { ProfileView, PasswordChangeView } from 'src/sections/account';
import CashierView, { TransactionView } from 'src/sections/cashier';
import RewardsView, { BonusesView, CashbackView, AchievementRewardsView } from 'src/sections/rewards';
import NotificationsView from 'src/sections/notifications';
import AboutUsView from 'src/sections/about-us';

// theme
import { paper } from 'src/theme/css';
import { useSettingsContext } from 'src/components/settings';
//
import Iconify from 'src/components/iconify';
import Scrollbar from 'src/components/scrollbar';
import { NAV } from 'src/layouts/config-layout';


const MainDrawer = () => {
  const theme = useTheme();

  const { isLoggedIn } = useSelector((state) => state.auth);
  const { name, editPage } = useSelector((state) => state.drawer);
  const dispatch = useDispatch();

  const [componentToRender, setComponentToRender] = useState<JSX.Element>();
  const [openDrawer, setOpenDrawer] = useState(false);
  const settings = useSettingsContext();
  const isMini = settings.themeLayout === 'mini';
  const lgUp = useResponsive('up', 'lg');

  useEffect(() => {
    if (name) setOpenDrawer(true);
  }, [name]);

  const handleCloseDrawer = () => {
    dispatch(closeDrawer());
    setOpenDrawer(false);
  };

  const handleOpenEditPage = useCallback(async (value: string) => {
    dispatch(openEditPage(value));
  }, [dispatch]);

  const handleCloseEditPage = () => {
    dispatch(closeEditPage());
  };

  useEffect(() => {
    if (name === 'account') {
      if (editPage === 'profile') {
        setComponentToRender(<ProfileView />);
      } else if (editPage === 'password') {
        setComponentToRender(<PasswordChangeView />);
      } else {
        setComponentToRender(<AccountView setEditPage={(value: string) => handleOpenEditPage(value)} />);
      }
    } else if (name === 'cashier') {
      if (editPage === 'transactions') {
        setComponentToRender(<TransactionView />);
      } else {
        setComponentToRender(<CashierView setEditPage={(value: string) => handleOpenEditPage(value)} />);
      }
    } else if (name === 'rewards') {
      if (editPage === 'bonuses') {
        setComponentToRender(<BonusesView />);
      } else if (editPage === 'cashback') {
        setComponentToRender(<CashbackView />);
      } else if (editPage === 'achievement') {
        setComponentToRender(<AchievementRewardsView />);
      } else {
        setComponentToRender(<RewardsView setEditPage={(value: string) => handleOpenEditPage(value)} />);
      }
    } else if (name === 'notifications') {
      setComponentToRender(<NotificationsView />);
    } else if (name === 'about us') {
      setComponentToRender(<AboutUsView />);
    } else {
      setComponentToRender(<Box />);
    }
  }, [name, editPage, handleOpenEditPage]);

  const renderHead = (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      sx={{ py: 2, px: 2.5 }}
    >
      {
        editPage &&
        <Button
          startIcon={<Iconify icon="bx:arrow-back" />}
          onClick={() => { handleCloseEditPage() }}
          sx={{
            position: "absolute",
            '&:hover': {
              color: "#ee2b25",
              backgroundColor: "#f7002505"
            }
          }}
        >
          Back
        </Button>
      }

      <Typography variant="h6" sx={{ flexGrow: 1, textTransform: "uppercase", textAlign: "center" }}>
        {name}
      </Typography>

      <Button
        endIcon={<Iconify icon="mingcute:close-line" />}
        onClick={handleCloseDrawer}
        sx={{
          position: "absolute",
          right: "20px",
          '&:hover': {
            color: "#ee2b25",
            backgroundColor: "#f7002505"
          }
        }}
      >
        Close
      </Button>
    </Stack>
  );

  return (
    <Drawer
      anchor="left"
      open={openDrawer && isLoggedIn}
      // onClose={handleCloseDrawer}
      transitionDuration={{ enter: 250, exit: 0 }}
      slotProps={{
        backdrop: { invisible: false },
      }}
      sx={{
        [`& .${drawerClasses.paper}`]: {
          ...paper({ theme, bgcolor: theme.palette.background.default }),
          width: 600,
          maxWidth: 1,
          left: { md: isMini && lgUp ? NAV.W_MINI : NAV.W_VERTICAL },
          backgroundColor: 'rgb(237 237 238)',
          // backgroundColor: '#1a1d2c',
        },
      }}
    >
      {renderHead}

      {/* <Divider sx={{ borderStyle: 'dashed' }} /> */}

      <Scrollbar>{componentToRender}</Scrollbar>

      {/* <FullScreenOption /> */}
    </Drawer>
  );
}

export default MainDrawer;